export type OnboardingDiscoveryQuestionOptionV1 = {
	label: string;
	value: string;
};

export type OnboardingDiscoveryQuestionContinuationV1 = {
	version: 1;
	runRef: string;
	questionRef: string;
	workspaceRef: string;
	brandRef: string;
	question: string;
	options: OnboardingDiscoveryQuestionOptionV1[];
	allowCustomAnswer: boolean;
};

type DiscoveryRunProjection = {
	run_id?: unknown;
	workspace_id?: unknown;
	brand_id?: unknown;
	status?: unknown;
};

type DiscoveryQuestionProjection = {
	id?: unknown;
	run_id?: unknown;
	workspace_id?: unknown;
	brand_id?: unknown;
	question?: unknown;
	options_json?: unknown;
	allow_custom_answer?: unknown;
	status?: unknown;
};

const exactRef = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const projectOptions = (value: unknown): OnboardingDiscoveryQuestionOptionV1[] | null => {
	if (!Array.isArray(value)) return null;
	const projected: OnboardingDiscoveryQuestionOptionV1[] = [];
	const values = new Set<string>();
	const labels = new Set<string>();
	for (const candidate of value) {
		if (!candidate || typeof candidate !== 'object') return null;
		const record = candidate as Record<string, unknown>;
		const label = exactRef(record.label);
		const optionValue = exactRef(record.value);
		if (!label || !optionValue || labels.has(label) || values.has(optionValue)) return null;
		labels.add(label);
		values.add(optionValue);
		projected.push({ label, value: optionValue });
	}
	return projected;
};

export const projectOnboardingDiscoveryQuestionContinuation = ({
	run,
	questions
}: {
	run: DiscoveryRunProjection;
	questions: DiscoveryQuestionProjection[];
}): OnboardingDiscoveryQuestionContinuationV1 | null => {
	const runRef = exactRef(run.run_id);
	const workspaceRef = exactRef(run.workspace_id);
	const brandRef = exactRef(run.brand_id);
	if (!runRef || !workspaceRef || !brandRef || run.status !== 'needs_review') return null;

	const scoped = questions.map((question) => {
		const questionRef = exactRef(question.id);
		const text = exactRef(question.question);
		const options = projectOptions(question.options_json);
		if (
			!questionRef ||
			!text ||
			options === null ||
			question.run_id !== runRef ||
			question.workspace_id !== workspaceRef ||
			question.brand_id !== brandRef
		) {
			return null;
		}
		return { question, questionRef, text, options };
	});
	if (scoped.some((question) => question === null)) return null;
	const pending = scoped.find((item) => item?.question.status === 'pending');
	if (!pending) return null;

	return {
		version: 1,
		runRef,
		questionRef: pending.questionRef,
		workspaceRef,
		brandRef,
		question: pending.text,
		options: pending.options,
		allowCustomAnswer: pending.question.allow_custom_answer === true
	};
};

export const projectOnboardingDiscoveryQuestionFollowUps = (
	continuation: OnboardingDiscoveryQuestionContinuationV1
): string[] => continuation.options.map((option) => option.label);

export const sameOnboardingDiscoveryQuestion = (
	left: OnboardingDiscoveryQuestionContinuationV1,
	right: OnboardingDiscoveryQuestionContinuationV1
): boolean => {
	const signature = (question: OnboardingDiscoveryQuestionContinuationV1) => [
		question.version,
		question.runRef,
		question.questionRef,
		question.workspaceRef,
		question.brandRef,
		question.question,
		question.allowCustomAnswer,
		question.options.map(({ label, value }) => [label, value])
	];
	return JSON.stringify(signature(left)) === JSON.stringify(signature(right));
};
